/**
 * The autonomy level (decision 0048): how far an agent goes in a turn that changes started. A turn a person started
 * (and every turn from before this wave, without `startedBy`) uses the agent's full policy. The level is read at every
 * command, so a switch takes effect at the next command.
 */
import { openReviewCards } from "./outputs"
import { agentOf, artifactOf, runOf } from "./state"
import type { Actor, Agent, Autonomy, Command, ProjectState } from "./types"

/** The agent's level; absent = "act" (today's behaviour). */
export const levelOf = (a: Pick<Agent, "autonomy">): Autonomy => a.autonomy ?? "act"

/**
 * The level that limits this command, or null for the full policy (decision 0048): null for people, workers, runners,
 * the system, the Mayor, a run that is not a turn of this agent, and a turn a person started (or one without
 * `startedBy`, from before this wave). There is deliberately no "ended run" check: the level holds for any turn run of
 * this agent with `startedBy: "changes"`, so it never fails open to the full policy.
 */
export const autonomyOf = (s: ProjectState, actor: Actor): Autonomy | null => {
  if (actor._tag !== "agent" || s.mayor?.agentId === actor.agentId) return null
  const run = runOf(s, actor.runId)
  if (run?.kind !== "turn" || run.agentId !== actor.agentId || run.startedBy !== "changes") return null
  const agent = agentOf(s, actor.agentId)
  return agent ? levelOf(agent) : null
}

/** Labels and help texts of the three levels (Settings › Agents, the chat header chip, the turn file). */
export const AUTONOMY_TEXT: Readonly<
  Record<Autonomy, { readonly label: string; readonly help: (name: string) => string }>
> = {
  suggest: {
    label: "Suggest in chat",
    help: (n: string) => `${n} says what changed and what comes next. You decide by replying.`,
  },
  ask: {
    label: "Ask first",
    help: (n: string) => `${n} puts the next steps in Needs you. Nothing new starts until you approve.`,
  },
  act: {
    label: "Act, then tell me",
    help: (n: string) =>
      `${n} takes the next steps under its delegation and tells you. Selected policies and reserved human decisions still apply.`,
  },
}

/** Allowed at every level, in every turn (decision 0048): talk, review, record, brake, and run bookkeeping. */
const ALWAYS: ReadonlySet<Command["_tag"]> = new Set<Command["_tag"]>([
  "PostComment",
  "Ask",
  "Answer",
  "RecordReview",
  "VerifyProjectWork",
  "PostArtifact",
  "RecordKpiSample",
  "RecordKpiSamples",
  "SendBrief",
  "ReviseProposal",
  "WithdrawProposal",
  "EscalateProposal", // decision 0102: passing a card up with a recommendation decides nothing
  "CancelRun",
  "RequestRun",
  "RecordRunStarted",
  "RecordRunEnded",
  "ReportOutcome",
])

/** Refused at Ask first: they start or settle work without a card. All four are allowed `ask_human` option effects. */
const ASK_REFUSED: ReadonlySet<Command["_tag"]> = new Set<Command["_tag"]>([
  "RetryItem",
  "UnblockItem",
  "PauseItem",
  "AcceptImpossible",
])

/**
 * The sentence that refuses `tag` at `level`, or null when the level allows it. Decision 0070 (TRI-25): agents repeat
 * these to people, so they say the rule in plain words (no "changes started this turn", no "person-started").
 */
export const autonomyRefusal = (level: Autonomy, tag: Command["_tag"]): string | null => {
  if (level === "act" || ALWAYS.has(tag)) return null
  if (level === "suggest")
    return "Your level is Suggest in chat for things no person asked you to do: start nothing new. Write your suggestion in your reply; the person decides by replying."
  if (!ASK_REFUSED.has(tag)) return null
  return `Your level is Ask first for things no person asked you to do: put ${tag} on an \`ask_human\` card as an option instead; the person's click applies it.`
}

/**
 * The state-dependent part of the level (decision 0050): at Suggest and Ask first, a new version of an artifact on an
 * item with an open human review card would change what the person picks, so it is refused like starting something new.
 */
export const autonomyStateRefusal = (s: ProjectState, level: Autonomy, command: Command): string | null => {
  if (level === "act" || command._tag !== "PostArtifact" || command.supersedes === null) return null
  const old = artifactOf(s, command.supersedes)
  if (!old?.itemId || openReviewCards(s, old.itemId).length === 0) return null
  return `Your level is ${AUTONOMY_TEXT[level].label} for things no person asked you to do, and a new version would change the picks on the open review card. Say what you would change in your reply; the person decides.`
}
